import { patchDOM } from '@fc/dom-patch';

export const SECTION_ID_PREFIX = 'shopify-section-';

export function createAbortError() {
  return new DOMException('Aborted', 'AbortError');
}

export function isAbortError(error) {
  return error instanceof DOMException ? error.name === 'AbortError' : error?.name === 'AbortError';
}

export function buildSectionDomId(id) {
  if (typeof id !== 'string' || !id) {
    throw new Error('Section id is required');
  }

  return id.startsWith(SECTION_ID_PREFIX) ? id : `${SECTION_ID_PREFIX}${id}`;
}

export function parseHTMLDocument(source) {
  if (source instanceof Document) {
    return source;
  }

  if (typeof source !== 'string') {
    throw new Error('HTML source must be a string or Document');
  }

  return new DOMParser().parseFromString(source, 'text/html');
}

export function normalizeHTMLRoot(source) {
  if (!source) return null;

  if (source instanceof Document || source instanceof DocumentFragment || source instanceof Element) {
    return source;
  }

  if (typeof source !== 'string') {
    return null;
  }

  return parseHTMLDocument(source);
}

function resolveQueryScope(source, scope) {
  if (scope instanceof Document || scope instanceof DocumentFragment || scope instanceof Element) {
    return scope;
  }

  const root = normalizeHTMLRoot(source);
  if (root) return root;

  throw new Error('HTML source must be a string, Document, DocumentFragment, or Element');
}

export function findHTMLNode(source, selector, options = {}) {
  if (typeof selector !== 'string' || !selector) return null;
  return resolveQueryScope(source, options.scope).querySelector(selector);
}

export function findHTMLNodes(source, selector, options = {}) {
  if (typeof selector !== 'string' || !selector) return [];
  return Array.from(resolveQueryScope(source, options.scope).querySelectorAll(selector));
}

export function requireHTMLNode(source, selector, options = {}) {
  const node = findHTMLNode(source, selector, options);
  if (node) return node;

  throw new Error(options.message || `HTML node "${selector}" not found`);
}

export function importHTMLNode(source, selector, options = {}) {
  const node = options.required
    ? requireHTMLNode(source, selector, options)
    : findHTMLNode(source, selector, options);

  return node ? document.importNode(node, true) : null;
}

export function importHTMLNodes(source, selector, options = {}) {
  return findHTMLNodes(source, selector, options).map((node) => document.importNode(node, true));
}

export function findSectionNode(source, id, options = {}) {
  return findHTMLNode(source, `#${buildSectionDomId(id)}`, options);
}

export function patchHTML(target, source, selectorOrNode, options) {
  const next =
    typeof selectorOrNode === 'string' ? requireHTMLNode(source, selectorOrNode) : selectorOrNode;

  if (!(next instanceof Node)) {
    throw new Error('Patch source node not found');
  }

  patchDOM(target, next, options);
  return target;
}

export class LatestRequestController {
  #controller = null;
  #token = 0;

  start() {
    this.abort();

    const controller = new AbortController();
    const token = ++this.#token;
    this.#controller = controller;

    return {
      signal: controller.signal,
      abort: () => controller.abort(),
      isCurrent: () => this.#controller === controller && this.#token === token && !controller.signal.aborted,
      finish: () => {
        if (this.#controller === controller) {
          this.#controller = null;
        }
      },
    };
  }

  abort() {
    this.#controller?.abort();
    this.#controller = null;
  }
}
